import clsx from "clsx";

import type { DictationStatus } from "@/hooks/useDictation";

interface DictationStatusProps {
  variant: "cyan" | "magenta";
  status: DictationStatus;
  elapsedMs: number;
  level: number;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

const BAR_COUNT = 80;
const MIN_HEIGHT = 2;
const MAX_HEIGHT = 28;
const SILENCE_THRESHOLD = 0.01;

function buildWaveHeights(level: number, elapsedMs: number): number[] {
  if (level < SILENCE_THRESHOLD) {
    return Array.from({ length: BAR_COUNT }, () => MIN_HEIGHT);
  }

  // sqrt curve amplifies quiet speech so the waveform is visible at
  // typical mic levels (0.05–0.3) instead of requiring near-max input.
  const boosted = Math.sqrt(level);
  const phase = elapsedMs / 120;
  const range = MAX_HEIGHT - MIN_HEIGHT;

  return Array.from({ length: BAR_COUNT }, (_, i) => {
    const primary = Math.sin(phase + i * 0.7);
    const secondary = Math.sin(phase * 0.6 + i * 1.3) * 0.3;
    const tertiary = Math.sin(phase * 1.4 + i * 0.4) * 0.15;
    const normalized = ((primary + secondary + tertiary) / 1.45 + 1) / 2;
    return Math.max(MIN_HEIGHT, Math.round(MIN_HEIGHT + normalized * boosted * range));
  });
}

export function DictationStatus({
  variant,
  status,
  elapsedMs,
  level,
}: DictationStatusProps): React.JSX.Element | null {
  if (status === "idle") {
    return null;
  }

  if (status === "transcribing" || status === "processing") {
    return (
      <div className="border-cyber-border/40 bg-cyber-surface/20 mt-2 flex items-center gap-2 rounded-lg border px-3 py-2">
        <span className="animate-spin-glow text-cyber-muted text-xs">◐</span>
        <span className="text-cyber-muted text-[11px]">
          {status === "transcribing" ? "Transcribing..." : "Processing..."}
        </span>
      </div>
    );
  }

  const waveHeights = buildWaveHeights(level, elapsedMs);
  const barColor = variant === "cyan" ? "bg-cyber-cyan" : "bg-cyber-magenta";
  const barOpacity = Math.max(0.2, 0.2 + Math.sqrt(level) * 0.65);

  return (
    <div className="border-cyber-border/40 bg-cyber-surface/20 mt-2 flex items-center gap-3 rounded-lg border px-3 py-2">
      <span className="animate-neon-pulse bg-cyber-red h-1.5 w-1.5 flex-shrink-0 rounded-full" />

      <div className="flex flex-1 items-center justify-between" aria-hidden="true">
        {waveHeights.map((height, i) => (
          <span
            key={i}
            className={clsx("w-[1.5px] rounded-full", barColor)}
            style={{
              height: `${height}px`,
              opacity: barOpacity,
              transition: "height 80ms ease-out",
            }}
          />
        ))}
      </div>

      <span className="text-cyber-muted flex-shrink-0 font-mono text-[11px] tabular-nums">
        {formatDuration(elapsedMs)}
      </span>
    </div>
  );
}
